import axios from 'axios';
import portalApiClient from './portalApiClient';
import { extractApiError } from '../utils/apiError';
import { API_BASE_URL } from '../utils/constants';
import { getPortalToken } from '../utils/portalStorage';

/**
 * Data access for the customer portal. Every call is scoped to the logged-in
 * customer by the backend (customer_id comes from the token — the FE never
 * sends it). All list endpoints accept { skip, limit, status, from_date,
 * to_date } (inquiries: no date filter).
 */

/** Build a clean query object, dropping empty values. */
const buildParams = ({ skip = 0, limit = 20, status, from_date, to_date } = {}) => {
  const params = { skip, limit };
  if (status) params.status = status;
  if (from_date) params.from_date = from_date;
  if (to_date) params.to_date = to_date;
  return params;
};

const ok = (res) => ({ success: true, data: res.data });
const fail = (error) => ({ success: false, error: extractApiError(error), status: error.response?.status });

// Short-lived in-memory cache for the picker lists (product-sizes / locations).
// These are re-fetched by every picker instance and on every keystroke-settle;
// caching identical queries avoids duplicate network calls within a session.
const pickerCache = new Map();
const PICKER_TTL = 5 * 60 * 1000; // 5 min
const cacheGet = (key) => {
  const hit = pickerCache.get(key);
  if (hit && (nowMs() - hit.t) < PICKER_TTL) return hit.v;
  return null;
};
const cacheSet = (key, v) => { pickerCache.set(key, { t: nowMs(), v }); };
// Date.now() is fine here (client-side, not a workflow); wrapped for clarity.
function nowMs() { return Date.now(); }

const portalDataService = {
  // --- Purchase Orders = what the customer BUYS from the company.
  // Backed by company SALES orders; order-shaped items use `units` (plural).
  async getPurchases(filters) {
    try { return ok(await portalApiClient.get('/purchase-orders', { params: buildParams(filters) })); }
    catch (e) { return fail(e); }
  },
  async getPurchase(orderId) {
    try { return ok(await portalApiClient.get(`/purchase-orders/${orderId}`)); }
    catch (e) { return fail(e); }
  },

  // --- Sales Orders = what the customer SELLS to the company.
  // Backed by company PURCHASE orders; items use `unit` (singular); carries
  // counterparty_company. NOTE: /portal/sales-orders may 404 until BE deploys it.
  async getSales(filters) {
    try { return ok(await portalApiClient.get('/sales-orders', { params: buildParams(filters) })); }
    catch (e) { return fail(e); }
  },
  async getSale(orderId) {
    try { return ok(await portalApiClient.get(`/sales-orders/${orderId}`)); }
    catch (e) { return fail(e); }
  },

  // --- Inquiries ---
  async getInquiries(filters) {
    // Inquiries support only skip/limit/status (no date filter).
    const { skip = 0, limit = 20, status } = filters || {};
    const params = { skip, limit };
    if (status) params.status = status;
    try { return ok(await portalApiClient.get('/inquiries', { params })); }
    catch (e) { return fail(e); }
  },
  async getInquiry(inquiryId) {
    try { return ok(await portalApiClient.get(`/inquiries/${inquiryId}`)); }
    catch (e) { return fail(e); }
  },
  /**
   * Create an inquiry. Body: { items:[{product_size_id,quantity}], location_id?,
   * expected_delivery_date?, remark? }. No customer_id, no price (staff quote later).
   */
  async createInquiry(body) {
    try { return ok(await portalApiClient.post('/inquiries', body)); }
    catch (e) { return fail(e); }
  },

  /**
   * Product-size picker for inquiry creation. OPEN BE ITEM: no portal-safe
   * product endpoint is confirmed yet. We try, in order:
   *   1. /portal/product-sizes  (preferred — a portal-scoped list)
   *   2. /product-sizes          (shared staff list, if it accepts the portal token)
   * If both fail (404/401/403), the create form falls back to a manual
   * product_size_id entry until BE exposes a portal product list.
   */
  async getProductSizes(search = '') {
    const cacheKey = `products::${search}`;
    const cached = cacheGet(cacheKey);
    if (cached) return cached;

    const token = getPortalToken();
    const headers = token ? { Authorization: `Bearer ${token}` } : {};
    const params = { search: search || undefined, limit: 50 };
    const normalize = (data) => (Array.isArray(data) ? data : (data?.items || data?.product_sizes || []));
    // 1) portal-scoped endpoint (via the portal client — same base + 401 handling)
    try {
      const res = await portalApiClient.get('/product-sizes', { params });
      const out = { success: true, data: normalize(res.data) };
      cacheSet(cacheKey, out);
      return out;
    } catch (e) {
      if (e.response && e.response.status !== 404) {
        return { success: false, error: extractApiError(e), status: e.response.status };
      }
      // 404 → endpoint not deployed; fall through to the staff list.
    }
    // 2) shared staff endpoint with the portal token
    try {
      const res = await axios.get(`${API_BASE_URL}/api/v1/product-sizes`, { params, headers });
      const out = { success: true, data: normalize(res.data) };
      cacheSet(cacheKey, out);
      return out;
    } catch (e) {
      return { success: false, error: extractApiError(e), status: e.response?.status };
    }
  },

  /**
   * Delivery-location picker for inquiry creation. Prefers the portal-scoped
   * `/portal/locations` (now the customer's own saved delivery locations),
   * falls back to the staff list, then to manual location_id entry.
   */
  async getLocations(search = '') {
    const cacheKey = `locations::${search}`;
    const cached = cacheGet(cacheKey);
    if (cached) return cached;

    const token = getPortalToken();
    const headers = token ? { Authorization: `Bearer ${token}` } : {};
    const params = { search: search || undefined, limit: 100 };
    const normalize = (data) => (Array.isArray(data) ? data : (data?.items || data?.locations || []));
    try {
      const res = await portalApiClient.get('/locations', { params });
      const out = { success: true, data: normalize(res.data) };
      cacheSet(cacheKey, out);
      return out;
    } catch (e) {
      if (e.response && e.response.status !== 404) {
        return { success: false, error: extractApiError(e), status: e.response.status };
      }
    }
    try {
      const res = await axios.get(`${API_BASE_URL}/api/v1/locations`, { params, headers });
      const out = { success: true, data: normalize(res.data) };
      cacheSet(cacheKey, out);
      return out;
    } catch (e) {
      return { success: false, error: extractApiError(e), status: e.response?.status };
    }
  },

  /** The customer's own saved delivery locations (profile page). */
  async getMyLocations() {
    try {
      const res = await portalApiClient.get('/my-locations');
      const data = res.data;
      return { success: true, data: Array.isArray(data) ? data : (data?.items || []) };
    } catch (e) {
      return { success: false, error: extractApiError(e), status: e.response?.status };
    }
  },

  /** Change the logged-in customer's password. */
  async changePassword(currentPassword, newPassword) {
    try {
      const res = await portalApiClient.post('/me/change-password', {
        current_password: currentPassword,
        new_password: newPassword,
      });
      return { success: true, data: res.data };
    } catch (e) {
      return { success: false, error: extractApiError(e), status: e.response?.status };
    }
  },

  /**
   * Submit / update the onboarding profile (the "signup" step).
   * PUT /portal/me/profile — all fields optional; gst_number (if given) must be
   * 15 chars (else 422). On success onboarding_status flips to profile_submitted;
   * returns the updated /me shape.
   */
  async updateProfile(body) {
    try {
      const res = await portalApiClient.put('/me/profile', body);
      return { success: true, data: res.data };
    } catch (e) {
      return { success: false, error: extractApiError(e), status: e.response?.status };
    }
  },
};

export default portalDataService;
